import type { ReactNode } from 'react';
import { MapPin, Wind, Mountain, Bed, ExternalLink, Thermometer, Waves, Clock, Route, TrendingUp } from 'lucide-react';
import { MapsWidget } from './MapsWidget';
import { WikilocWidget } from './WikilocWidget';
import { getPublished, getByVilarejo } from '@/data/loader';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';

interface EntityWidgetsProps {
  entity: any;
}

/* ─── Mock de previsão para praias (realistic) ─── */
function PrevisaoPraiaMock({ praiaNome }: { praiaNome: string }) {
  const { t } = useTranslation();
  const { trackOutbound } = useAnalytics();
  const tempAgua = Math.floor(Math.random() * 4) + 22; // 22-25°C
  const vento = Math.floor(Math.random() * 15) + 5; // 5-20 km/h
  const ondas = ['Calmas', 'Moderadas', 'Fortes'][Math.floor(Math.random() * 3)];

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
          <Thermometer className="w-4 h-4 text-teal-deep" />
        </div>
        <div>
          <h3 className="font-display text-base text-warm-dark">{t('entity.widgets.weather')}</h3>
          <p className="font-body text-[11px] text-warm-gray">
            {t('entity.widgets.weatherDesc', { title: praiaNome })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-sand-50 rounded-xl p-3 text-center">
          <Thermometer className="w-4 h-4 text-coral mx-auto mb-1" />
          <p className="font-display text-lg text-warm-dark">{tempAgua}°C</p>
          <p className="font-body text-[10px] text-warm-gray">Água</p>
        </div>
        <div className="bg-sand-50 rounded-xl p-3 text-center">
          <Wind className="w-4 h-4 text-teal-deep mx-auto mb-1" />
          <p className="font-display text-lg text-warm-dark">{vento}</p>
          <p className="font-body text-[10px] text-warm-gray">km/h vento</p>
        </div>
        <div className="bg-sand-50 rounded-xl p-3 text-center">
          <Waves className="w-4 h-4 text-ocean mx-auto mb-1" />
          <p className="font-display text-lg text-warm-dark">{ondas}</p>
          <p className="font-body text-[10px] text-warm-gray">Ondas</p>
        </div>
      </div>

      <a
        href="https://www.climatempo.com.br/previsao-do-tempo/cidade/757/paranagua-pr"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackOutbound('https://www.climatempo.com.br/previsao-do-tempo/cidade/757/paranagua-pr', 'Previsão do tempo Climatempo', 'widget_weather')}
        className="inline-flex items-center gap-1 text-sm text-teal-deep hover:text-teal-dark font-body font-semibold"
      >
        {t('entity.widgets.weatherCta')} <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

/* ─── Cards de pousadas reais ─── */
function PousadasReaisWidget({ vilarejo }: { vilarejo: string }) {
  const { t } = useTranslation();
  const { trackWhatsapp, trackAffiliate } = useAnalytics();
  const pousadas = getPublished('pousada');
  const doVilarejo = vilarejo
    ? pousadas.filter((p: any) => p.vilarejo === vilarejo || p.vilarejoName === vilarejo).slice(0, 3)
    : pousadas.slice(0, 3);

  if (doVilarejo.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
          <Bed className="w-4 h-4 text-amber-600" />
        </div>
        <div>
          <h3 className="font-display text-base text-warm-dark">{t('entity.widgets.accommodations')}</h3>
          <p className="font-body text-[11px] text-warm-gray">
            {t('entity.widgets.accommodationsDesc', { vilarejo: vilarejo || 'Ilha do Mel' })}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {doVilarejo.map((p: any) => (
          <a
            key={p.id}
            href={p.provider ? `https://wa.me/${p.provider.phone}?text=${encodeURIComponent(`Oi! Vi a ${p.title} no vemprailhadomel.com.br e gostaria de reservar.`)}` : undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-xl border border-sand-100 hover:border-teal-200 hover:bg-teal-50/30 transition-all group"
          >
            <div className="w-12 h-12 rounded-lg bg-sand-100 overflow-hidden flex-shrink-0">
              {p.image ? (
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              ) : (
                <Bed className="w-5 h-5 text-sand-300 m-3.5" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body text-sm font-semibold text-warm-dark group-hover:text-teal-deep transition-colors truncate">
                {p.title}
              </p>
              <p className="font-body text-[11px] text-warm-gray">
                {p.vilarejoName} {p.priceRange?.min && `· R$ ${p.priceRange.min}–${p.priceRange.max}`}
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-sand-300 group-hover:text-teal-deep transition-colors flex-shrink-0" />
          </a>
        ))}
      </div>

      <a
        href="https://www.booking.com/searchresults.html?ss=Ilha+do+Mel%2C+Paran%C3%A1"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackAffiliate({ provider: 'Booking.com', platform: 'booking', productType: 'hotel' })}
        className="mt-3 flex items-center justify-center gap-2 text-ocean font-body text-sm font-semibold hover:underline"
      >
        {t('entity.widgets.bookingCta')} <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

/* ─── Info do mapa com contexto ─── */
function MapaComContexto({ lat, lng, title, entity }: { lat: number; lng: number; title: string; entity: any }) {
  const { t } = useTranslation();

  // Determinar distância aproximada até o trapiche mais próximo
  const trapiches = getPublished('trapiche');
  const trapicheProximo = trapiches.find((t: any) => t.vilarejo === entity.vilarejo);

  return (
    <div>
      <MapsWidget lat={lat} lng={lng} title={title} height={300} />
      <div className="mt-3 grid grid-cols-2 gap-2">
        {trapicheProximo && (
          <div className="bg-sand-50 rounded-lg px-3 py-2 flex items-center gap-2">
            <Route className="w-3 h-3 text-teal-deep" />
            <span className="font-body text-[11px] text-warm-gray">
              Trapiche mais próximo: <span className="font-semibold text-warm-dark">{trapicheProximo.title}</span>
            </span>
          </div>
        )}
        {entity.access && (
          <div className="bg-sand-50 rounded-lg px-3 py-2 flex items-center gap-2">
            <Clock className="w-3 h-3 text-teal-deep" />
            <span className="font-body text-[11px] text-warm-gray">
              Acesso: <span className="font-semibold text-warm-dark">{entity.access}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Wikiloc com estatísticas ─── */
function WikilocComStats({ trailId, entity }: { trailId: string; entity: any }) {
  const { t } = useTranslation();

  return (
    <div>
      <WikilocWidget trailId={trailId} height={400} elevation />
      {entity.difficulty && (
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 bg-sand-50 rounded-lg px-3 py-2 flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-teal-deep" />
            <span className="font-body text-[11px] text-warm-gray">
              Dificuldade: <span className="font-semibold text-warm-dark">{entity.difficulty}</span>
            </span>
          </div>
          {entity.duration && (
            <div className="flex-1 bg-sand-50 rounded-lg px-3 py-2 flex items-center gap-2">
              <Clock className="w-3 h-3 text-teal-deep" />
              <span className="font-body text-[11px] text-warm-gray">
                Duração: <span className="font-semibold text-warm-dark">{entity.duration}</span>
              </span>
            </div>
          )}
          {entity.distance && (
            <div className="flex-1 bg-sand-50 rounded-lg px-3 py-2 flex items-center gap-2">
              <Route className="w-3 h-3 text-teal-deep" />
              <span className="font-body text-[11px] text-warm-gray">
                Distância: <span className="font-semibold text-warm-dark">{entity.distance}</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function EntityWidgets({ entity }: EntityWidgetsProps) {
  const { t } = useTranslation();
  const widgets: ReactNode[] = [];

  // 1. Mapa para entidades com coordenadas
  const geo = entity.schemaOrg?.geo;
  if (geo?.latitude && geo?.longitude) {
    widgets.push(
      <div key="map" className="mb-8">
        <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
          <MapPin className="w-5 h-5 text-teal-deep" />
          {t('entity.widgets.location')}
        </h3>
        <MapaComContexto lat={geo.latitude} lng={geo.longitude} title={entity.title} entity={entity} />
      </div>
    );
  }

  // 2. Wikiloc para trilhas com trailId
  if (entity.category === 'trilha' && entity.trailId) {
    widgets.push(
      <div key="wikiloc" className="mb-8">
        <h3 className="flex items-center gap-2 font-display text-lg mb-3 text-warm-dark tracking-wide">
          <Mountain className="w-5 h-5 text-teal-deep" />
          {t('entity.widgets.wikiloc')}
        </h3>
        <WikilocComStats trailId={entity.trailId} entity={entity} />
      </div>
    );
  }

  // 3. Previsão do tempo para praias
  if (entity.category === 'praia') {
    widgets.push(
      <div key="weather" className="mb-8">
        <PrevisaoPraiaMock praiaNome={entity.title} />
      </div>
    );
  }

  // 4. Busca de hospedagem para pousadas
  if (entity.category === 'pousada') {
    widgets.push(
      <div key="booking" className="mb-8">
        <PousadasReaisWidget vilarejo={entity.vilarejoName || entity.vilarejo} />
      </div>
    );
  }

  if (widgets.length === 0) return null;

  return <div className="mt-8">{widgets}</div>;
}
